/**
 * VerificationFunnel.jsx — Iter-315l
 *
 * Admin dashboard for IDV verification tracking. Renders the full
 * funnel (Not Started → In Progress → Awaiting Review → Approved /
 * Rejected + Abandoned) as clickable count cards plus a searchable
 * per-bucket user list.
 *
 * Route: /admin/verification-funnel  (mounted in App.js)
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import {
  ShieldCheck, ShieldAlert, ShieldQuestion, ShieldX, Hourglass,
  Loader2, Search, Users, TrendingUp, Ban, ExternalLink, Bell,
} from 'lucide-react';
import { toast } from 'sonner';

const BUCKETS = [
  { code: 'not_started',     label: 'Not Started',     Icon: ShieldQuestion, tone: 'bg-slate-50 text-slate-700 border-slate-300' },
  { code: 'in_progress',     label: 'In Progress',     Icon: Hourglass,      tone: 'bg-amber-50 text-amber-800 border-amber-300' },
  { code: 'awaiting_review', label: 'Awaiting Review', Icon: ShieldAlert,    tone: 'bg-cyan-50 text-cyan-800 border-cyan-300' },
  { code: 'approved',        label: 'Approved',        Icon: ShieldCheck,    tone: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  { code: 'rejected',        label: 'Rejected',        Icon: ShieldX,        tone: 'bg-rose-50 text-rose-800 border-rose-300' },
  { code: 'abandoned',       label: 'Abandoned',       Icon: Ban,            tone: 'bg-red-50 text-red-800 border-red-300' },
];

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

export default function VerificationFunnel() {
  const [summary, setSummary] = useState(null);
  const [bucket, setBucket] = useState('in_progress');
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [nudging, setNudging] = useState(null);

  useEffect(() => {
    api.get('/admin/verification-funnel/summary')
      .then((r) => setSummary(r.data))
      .catch(() => toast.error('Could not load funnel'));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { bucket, limit: 50, ...(q ? { q } : {}) };
    api.get('/admin/verification-funnel/users', { params })
      .then((r) => setUsers(r.data.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [bucket, q]);

  const total = summary?.total_registered || 0;
  const cards = useMemo(() => BUCKETS.map((b) => ({
    ...b,
    count: summary?.buckets?.[b.code] || 0,
    share: total > 0 ? (summary?.buckets?.[b.code] || 0) / total : 0,
  })), [summary, total]);

  const handleNudge = async (user) => {
    setNudging(user.user_id);
    try {
      const { data } = await api.post(`/admin/verification-funnel/nudge/${user.user_id}`);
      const channels = (data.channels || []).join(', ');
      toast.success(channels ? `Nudge sent via ${channels}` : `Nudge queued for ${user.email}`);
    } catch (e) {
      toast.error('Could not send nudge');
    } finally {
      setNudging(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-testid="verification-funnel">
      <header>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-cyan-600" /> Verification Funnel
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Track every talent through IDV: from sign-up to approval. Click a card to inspect the queue.
        </p>
      </header>

      {/* Top metrics */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid="funnel-total-registered">
            <Users className="w-8 h-8 text-slate-500" />
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Total Registered</div>
              <div className="text-2xl font-bold text-slate-900">{total.toLocaleString()}</div>
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="funnel-completion-rate">
            <TrendingUp className="w-8 h-8 text-emerald-600" />
            <div>
              <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Completion Rate</div>
              <div className="text-2xl font-bold text-emerald-900">{pct(summary.completion_rate)}</div>
            </div>
          </div>
          <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4 flex items-center gap-3" data-testid="funnel-approval-rate">
            <ShieldCheck className="w-8 h-8 text-cyan-600" />
            <div>
              <div className="text-xs uppercase tracking-wide text-cyan-700 font-semibold">Approval Rate</div>
              <div className="text-2xl font-bold text-cyan-900">{pct(summary.approval_rate)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Bucket cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(({ code, label, Icon, tone, count, share }) => {
          const isActive = bucket === code;
          return (
            <button
              key={code}
              type="button"
              onClick={() => setBucket(code)}
              className={`rounded-xl border-2 p-3 text-left transition ${tone} ${
                isActive ? 'ring-2 ring-offset-1 ring-cyan-500 shadow-md' : 'hover:shadow'
              }`}
              data-testid={`funnel-bucket-${code}`}
            >
              <div className="flex items-center justify-between">
                <Icon className="w-5 h-5" />
                <span className="text-[10px] uppercase font-bold tracking-wide">{pct(share)}</span>
              </div>
              <div className="mt-1 text-2xl font-bold">{count.toLocaleString()}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wide">{label}</div>
            </button>
          );
        })}
      </div>

      {/* Search + user list */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="p-3 border-b border-slate-100 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, or phone…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200"
              data-testid="funnel-search"
            />
          </div>
          <span className="text-xs text-slate-500 shrink-0">
            {BUCKETS.find((b) => b.code === bucket)?.label} · {users.length}
          </span>
        </div>

        {loading ? (
          <div className="p-6 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="funnel-empty">
            No talent in this bucket.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="funnel-user-list">
            {users.map((u) => (
              <li key={u.user_id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">
                    {u.full_name || u.email}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {u.email}
                    {u.city && u.state ? ` · ${u.city}, ${u.state}` : ''}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    IDV status: <span className="font-semibold text-slate-700">{u.idv_status || 'not set'}</span>
                    {u.days_stuck != null && u.days_stuck >= 7 && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold text-[10px] uppercase">
                        stuck {u.days_stuck}d
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <a
                    href={`/admin/talent/${u.user_id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    data-testid={`funnel-user-view-${u.user_id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> Profile
                  </a>
                  {(bucket === 'not_started' || bucket === 'in_progress' || bucket === 'abandoned') && (
                    <button
                      type="button"
                      onClick={() => handleNudge(u)}
                      disabled={nudging === u.user_id}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-cyan-400 text-xs font-semibold text-cyan-800 bg-cyan-50 hover:bg-cyan-100"
                      data-testid={`funnel-user-nudge-${u.user_id}`}
                    >
                      {nudging === u.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                      Nudge
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
